import React from 'react'

import {
  Descriptions,
  Result,
  Space,
  Tag,
  Typography,
} from 'antd'
import useSWR from 'swr'

import {
  fetchByDomain,
  fetchDetail,
} from '../../../api'
import { emptyCharacter } from '../../../components/EmptyChar'
import { getEnvironmentHost } from '../../../util/environment'
import { messages } from '../messages'

const { Item } = Descriptions

interface Props {
  tenantId: number
}

const renderTag = ({ label, color }: { label: string; color?: string }) => (
  <Tag key={label} color={color || 'purple'}>
    {label}
  </Tag>
)

const renderYesOrNo = ({ showTag, color }: { showTag: boolean; color?: string }) =>
  showTag ? renderTag({ color, label: 'Yes' }) : 'No'

export const Tenant = ({ tenantId }: Props) => {
  const { data: tenant }: any = useSWR(`/tenants/${tenantId}`, fetchDetail, { suspense: true })
  const { data: issuers, error } = useSWR(tenant ? ['/issuers', tenant?.domain] : null, fetchByDomain, {
    suspense: true,
  })

  if (error) {
    return <Result status="error" title={messages.tenant.retrieve.error} />
  }

  const [currentIssuer] = issuers?.filter((issuer: any) => issuer.id === tenant.issuerId)

  return (
    <>
      <Descriptions title={tenant.name}>
        <Item label="Domain">
          <Typography.Link href={getEnvironmentHost({ domain: tenant.domain })} target="_blank">
            {tenant.domain}
          </Typography.Link>
        </Item>
        <Item label="Is Trial">{renderYesOrNo({ showTag: tenant.isTrial, color: 'red' })}</Item>
        <Item label="Is Demo">{renderYesOrNo({ showTag: tenant.isDemo, color: 'red' })}</Item>
        <Item label="Segment Code">{tenant.segmentCode || emptyCharacter}</Item>
        <Item label="Issuer">{currentIssuer?.name || tenant.issuerId || emptyCharacter}</Item>
        <Item label="T+N">{tenant.defaultTPlusNDays}</Item>
        <Item label="Currency">{tenant.currency}</Item>
        <Item label="Timezone">{tenant.timezone}</Item>
        <Item label="Pipedrive Org Id">{tenant.pipelineOrgId || emptyCharacter}</Item>
        <Item label="Has Separation of Duties">{renderYesOrNo({ showTag: tenant.enforceSeparationOfDuties })}</Item>
        <Item label="Signoff Count" span={2}>
          {tenant.requiredRfqSignOffCount}
        </Item>
        <Item label="Has Capital Basis">{renderYesOrNo({ showTag: tenant.capitalBasisEnabled })}</Item>
        <Item label="Has Long Term Compliances" span={2}>
          {renderYesOrNo({ showTag: tenant.longTermComplianceOnly })}
        </Item>
        <Item label="LEI Code">{tenant.leiCode || emptyCharacter}</Item>
        <Item label="Clear Code">{tenant.clearCode || emptyCharacter}</Item>
      </Descriptions>
      <Space direction="vertical">
        <div>
          <Typography.Title level={5}>Licences</Typography.Title>
          <Space wrap>
            {tenant.licences.length ? tenant.licences.map((label: string) => renderTag({ label })) : 'No licences'}
          </Space>
        </div>
        <div>
          <Typography.Title level={5}>Back Office Email</Typography.Title>
          <Space wrap>
            {tenant.backOfficeEmail
              ? tenant.backOfficeEmail.split(',').map((label: string) => renderTag({ label }))
              : 'No back office email'}
          </Space>
        </div>
      </Space>
    </>
  )
}
